import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { fmtDate, ACTION_STATUS, statusLabel } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { WorkflowSteps } from "@/components/WorkflowSteps";
import { useTableSort, SortTh, useColumns, ColumnPicker, ExportCsvButton } from "@/components/SortableHeader";
import { AiResultDialog, useAiRunner } from "@/components/ai/AiResultDialog";
import { AiRemediationPanel } from "@/components/ai/AiRemediationPanel";
import { Eye, Lock, Search, RotateCw, Clock, PlugZap, Sparkles } from "lucide-react";

const TYPE_LABELS = {
  reconcileFix: "Drift remediation", autoRemediateFix: "Auto-remediation", onboard: "Onboarding", offboard: "Offboarding",
  update: "Update user", updateUser: "Update user", offboardUser: "Offboarding",
  enableUser: "Enable user", disableUser: "Disable user",
  deleteUser: "Delete user", applyTemplate: "Apply role template",
  delete: "Delete user", membership: "Group membership", rollback: "Rollback",
  temporaryAccessPass: "Temporary Access Pass",
  licenseActivate: "License activated", licenseClear: "License removed", licenseBuild: "Build license coverage",
  licenseAddon: "License add-on",
  passwordReset: "Password reset", adminPasswordReset: "Console password reset",
  passwordChange: "Console password change", passwordResetRequest: "Password reset request",
  accessModeChange: "Access mode change",
  ssoLogin: "SSO sign-in", ssoLoginFailed: "SSO sign-in blocked", ssoConsent: "Graph admin consent",
};
const typeLabel = (ty) => TYPE_LABELS[ty] || ty;
const fmtVal = (v) => v === null || v === undefined || v === "" ? "—" : Array.isArray(v) ? v.join(", ") : String(v);

// Shared "View" dialog (trigger + content) used by both the desktop table and the mobile cards.
function WfViewDialog({ w, t }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid={`view-workflow-${w.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><Eye size={14} /> {t("actions.view")}</button>
      </DialogTrigger>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid={`wf-detail-${w.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm">{w.title} · {statusLabel(w.status, t)}</DialogTitle>
          <DialogDescription className="font-mono text-[11px]">{w.type} · created {fmtDate(w.createdAt)} · by {w.createdBy}</DialogDescription>
        </DialogHeader>
        {w.meta?.diff?.length > 0 && (
          <div className="rounded-sm border border-zinc-200" data-testid={`wf-diff-${w.id}`}>
            <div className="border-b border-zinc-100 bg-zinc-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{t("actions.propertyChanges")}</div>
            <div className="divide-y divide-zinc-100">
              {w.meta.diff.map((d, i) => (
                <div key={i} className="grid grid-cols-[130px_1fr] gap-2 px-3 py-1.5 text-xs" data-testid={`wf-diff-row-${d.field}`}>
                  <span className="font-semibold text-zinc-700">{d.field}</span>
                  <span className="flex flex-wrap items-center gap-1.5 font-mono">
                    <span className="text-zinc-400 line-through">{fmtVal(d.old)}</span>
                    <span className="text-zinc-300">→</span>
                    <span className="font-semibold text-emerald-700">{fmtVal(d.new)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        <WorkflowSteps wf={w} />
      </DialogContent>
    </Dialog>
  );
}

// Status badges (+ timed-out / agent-offline pills) shared by table + cards.
function WfStatusBadges({ w, t }) {
  return (
    <>
      <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ACTION_STATUS[w.status] || ""}`}>{statusLabel(w.status, t)}</span>
      {w.timedOut && (
        <span className="ml-1.5 inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700" data-testid={`timedout-badge-${w.id}`}>
          <Clock size={11} /> {t("actions.timedOut")}
        </span>
      )}
      {w.agentOffline && (
        <span className="ml-1.5 inline-flex items-center gap-1 border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700" data-testid={`agentoffline-badge-${w.id}`}>
          <PlugZap size={11} /> {t("actions.agentOffline")}
        </span>
      )}
    </>
  );
}

// Quick-filter categories: group related event types into one-click chips.
const CATEGORIES = [
  { key: "all", types: null, always: true },
  { key: "tap", types: ["temporaryAccessPass"], always: true },
  { key: "license", types: ["licenseActivate", "licenseClear", "licenseBuild", "licenseAddon"], always: true },
  { key: "password", types: ["passwordReset", "adminPasswordReset", "passwordChange", "passwordResetRequest"] },
  { key: "lifecycle", types: ["onboard", "offboard", "offboardUser", "update", "updateUser", "enableUser", "disableUser", "deleteUser", "delete", "membership", "rollback", "applyTemplate"] },
  { key: "sync", types: ["reconcileFix", "autoRemediateFix"] },
  { key: "access", types: ["accessModeChange"] },
  { key: "sso", types: ["ssoLogin", "ssoLoginFailed", "ssoConsent"] },
];

export default function Actions() {
  const { t, lang } = useLang();
  const { isAdmin, ai } = useAuth();
  const aiRunner = useAiRunner();
  const aiOn = !!(ai?.available && ai?.canUse);
  const [params] = useSearchParams();
  const timedOutOnly = params.get("timedout") === "1";
  const [wfs, setWfs] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [retrying, setRetrying] = useState("");
  const sort = useTableSort("createdAt", "desc", "actions");
  const cols = useColumns("actions");

  const load = () => api.get("/workflows?mode_all=true&limit=300").then((r) => setWfs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const retryWf = async (wid) => {
    setRetrying(wid);
    try {
      await api.post(`/workflows/${wid}/retry`);
      toast.success(t("actions.retryStarted"));
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRetrying(""); }
  };

  const types = useMemo(() => Array.from(new Set(wfs.map((w) => w.type))).sort(), [wfs]);
  const statuses = useMemo(() => Array.from(new Set(wfs.map((w) => w.status))).sort(), [wfs]);
  const catCounts = useMemo(() => {
    const m = {};
    for (const c of CATEGORIES) m[c.key] = c.types ? wfs.filter((w) => c.types.includes(w.type)).length : wfs.length;
    return m;
  }, [wfs]);
  const activeCat = CATEGORIES.find((c) => c.key === category);
  const filtered = useMemo(() => {
    const rows = wfs.filter((w) => {
      if (timedOutOnly && !w.timedOut) return false;
      if (activeCat?.types && !activeCat.types.includes(w.type)) return false;
      if (type !== "all" && w.type !== type) return false;
      if (status !== "all" && w.status !== status) return false;
      if (!q.trim()) return true;
      const hay = `${w.title} ${w.type} ${w.createdBy} ${w.displayName || ""}`.toLowerCase();
      return hay.includes(q.trim().toLowerCase());
    });
    return sort.sortRows(rows, { workflow: (w) => w.title });
  }, [wfs, q, type, status, sort, timedOutOnly, activeCat]);
  const csvHeaders = [
    { key: "title", label: "Workflow" },
    { key: "type", label: "Type" },
    ...(cols.show("by") ? [{ key: "by", label: "By" }] : []),
    ...(cols.show("when") ? [{ key: "when", label: "When" }] : []),
    { key: "status", label: "Status" },
  ];
  const csvRows = filtered.map((w) => ({ title: w.title, type: typeLabel(w.type), by: w.createdBy, when: fmtDate(w.createdAt), status: w.status }));

  return (
    <>
      <PageHeader title={t("actions.title")} subtitle={t("actions.subtitle")} />
      <div className="p-6 md:p-10">
        <AiRemediationPanel />
        <div className="mb-4 flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-4 py-2.5 text-xs text-zinc-500" data-testid="actionlog-immutable-note">
          <Lock size={13} className="shrink-0 text-zinc-400" /> {t("actions.immutableNote")}
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="actionlog-category-chips">
          {CATEGORIES.filter((c) => c.always || catCounts[c.key] > 0).map((c) => (
            <button
              key={c.key}
              data-testid={`actionlog-cat-${c.key}`}
              onClick={() => { setCategory(c.key); setType("all"); }}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                category === c.key
                  ? "border-primary bg-primary text-white"
                  : "border-zinc-300 bg-white text-zinc-600 hover:border-primary/50 hover:bg-primary/5"
              }`}
            >
              {t(`actions.cat.${c.key}`)}
              <span className={`font-mono text-[10px] ${category === c.key ? "text-white/70" : "text-zinc-400"}`}>{catCounts[c.key]}</span>
            </button>
          ))}
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative w-full max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by actor, title…" data-testid="actionlog-search" className="rounded-sm border-zinc-300 pl-9 focus-visible:ring-2 focus-visible:ring-primary" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="font-mono text-xs text-zinc-400" data-testid="actionlog-count">{filtered.length} / {wfs.length}</span>
            {aiOn && (
              <button data-testid="ai-summarize-actions" onClick={() => aiRunner.run(t("ai.summaryTitle"), "/ai/summarize-actions", { language: lang, limit: 60 })}
                className="inline-flex items-center gap-1.5 rounded-sm border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/10">
                <Sparkles size={13} /> {t("ai.summarizeActions")}
              </button>
            )}
            <ColumnPicker label={t("cols.columns")} cols={cols} columns={[{ key: "by", label: "By" }, { key: "when", label: "When" }]} />
            <ExportCsvButton label={t("cols.export")} filename="action-log.csv" headers={csvHeaders} rows={csvRows} />
          </div>
        </div>
        <div className="hidden border border-zinc-200 bg-white md:block">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                <SortTh label="Workflow" sortKey="workflow" sort={sort} filter={{ value: type, onChange: setType, allValue: "all", options: [{ value: "all", label: "All event types" }, ...types.map((ty) => ({ value: ty, label: typeLabel(ty) }))] }} />
                {cols.show("by") && <SortTh label="By" sortKey="createdBy" sort={sort} className="hidden md:table-cell" />}
                {cols.show("when") && <SortTh label="When" sortKey="createdAt" sort={sort} className="hidden lg:table-cell" />}
                <SortTh label="Status" sortKey="status" sort={sort} filter={{ value: status, onChange: setStatus, allValue: "all", options: [{ value: "all", label: t("filter.allStatuses") }, ...statuses.map((s) => ({ value: s, label: statusLabel(s, t) }))] }} />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-400">No matching workflows.</td></tr>}
              {filtered.map((w) => (
                <tr key={w.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`workflow-row-${w.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-zinc-900">{w.title}</div>
                    <div className="font-mono text-[11px] text-zinc-400">{typeLabel(w.type)} · {w.steps.length} step(s)</div>
                  </td>
                  {cols.show("by") && <td className="hidden px-4 py-3 text-sm text-zinc-600 md:table-cell">{w.createdBy}</td>}
                  {cols.show("when") && <td className="hidden px-4 py-3 text-sm text-zinc-500 lg:table-cell">{fmtDate(w.createdAt)}</td>}
                  <td className="px-4 py-3">
                    <WfStatusBadges w={w} t={t} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && (w.timedOut || w.agentOffline) && w.status === "error" && (
                      <button data-testid={`retry-workflow-${w.id}`} onClick={() => retryWf(w.id)} disabled={retrying === w.id}
                        title={t("actions.retryTimedOutHint")}
                        className="mr-3 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline disabled:opacity-50">
                        <RotateCw size={14} className={retrying === w.id ? "animate-spin" : ""} /> {t("actions.retry")}
                      </button>
                    )}
                    {aiOn && (
                      <button data-testid={`ai-explain-workflow-${w.id}`} onClick={() => aiRunner.run(t("ai.explainTitle"), "/ai/explain-workflow", { workflowId: w.id, language: lang })}
                        title={t(w.status === "error" ? "ai.explainFailure" : "ai.explain")}
                        className="mr-3 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                        <Sparkles size={13} /> {t(w.status === "error" ? "ai.explainFailure" : "ai.explain")}
                      </button>
                    )}
                    <WfViewDialog w={w} t={t} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="space-y-3 md:hidden" data-testid="actionlog-mobile-list">
          {filtered.length === 0 && <div className="border border-zinc-200 bg-white px-4 py-10 text-center text-sm text-zinc-400">No matching workflows.</div>}
          {filtered.map((w) => (
            <div key={w.id} className="border border-zinc-200 bg-white p-4" data-testid={`workflow-card-${w.id}`}>
              <div className="text-sm font-semibold text-zinc-900">{w.title}</div>
              <div className="mt-0.5 font-mono text-[11px] text-zinc-400">{typeLabel(w.type)} · {w.steps.length} step(s)</div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <WfStatusBadges w={w} t={t} />
              </div>
              <div className="mt-2 font-mono text-[11px] text-zinc-400">{w.createdBy} · {fmtDate(w.createdAt)}</div>
              <div className="mt-3 flex items-center gap-4 border-t border-zinc-100 pt-3">
                {isAdmin && (w.timedOut || w.agentOffline) && w.status === "error" && (
                  <button data-testid={`m-retry-workflow-${w.id}`} onClick={() => retryWf(w.id)} disabled={retrying === w.id}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline disabled:opacity-50">
                    <RotateCw size={14} className={retrying === w.id ? "animate-spin" : ""} /> {t("actions.retry")}
                  </button>
                )}
                {aiOn && (
                  <button data-testid={`m-ai-explain-workflow-${w.id}`} onClick={() => aiRunner.run(t("ai.explainTitle"), "/ai/explain-workflow", { workflowId: w.id, language: lang })}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                    <Sparkles size={13} /> {t(w.status === "error" ? "ai.explainFailure" : "ai.explain")}
                  </button>
                )}
                <WfViewDialog w={w} t={t} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <AiResultDialog open={aiRunner.state.open} onOpenChange={(v) => !v && aiRunner.close()} title={aiRunner.state.title} loading={aiRunner.state.loading} error={aiRunner.state.error} content={aiRunner.state.content} sources={aiRunner.state.sources} empty={aiRunner.state.empty} />
    </>
  );
}
